import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BarChart3,
  Boxes,
  ClipboardList,
  Database,
  FileCheck2,
  FileText,
  FileUp,
  FolderKanban,
  Hammer,
  LayoutDashboard,
  Plus,
  HelpCircle,
  Settings,
  ShieldCheck,
  Users,
} from "lucide-react";

import OrzioLogo from "@/components/brand/OrzioLogo";
import LanguageToggle from "@/components/LanguageToggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { getPlatformAdminStatus } from "@/modules/admin/services/adminPlatformService";

interface SidebarNavItem {
  labelKey: string;
  to?: string;
  icon: typeof LayoutDashboard;
  match?: (pathname: string) => boolean;
  badge?: string;
  requiresPlatformAdmin?: boolean;
  subItems?: SidebarNavSubItem[];
}

interface SidebarNavSubItem {
  labelKey: string;
  to: string;
  icon?: typeof LayoutDashboard;
  match?: (pathname: string) => boolean;
}

interface SidebarNavSection {
  labelKey: string;
  items: SidebarNavItem[];
}

export interface AppSidebarProps {
  onNewBudgetClick?: () => void;
  className?: string;
}

const NAV_SECTIONS: SidebarNavSection[] = [
  {
    labelKey: "nav.section.management",
    items: [
      {
        labelKey: "Admin",
        to: "/admin",
        icon: ShieldCheck,
        match: (pathname) => pathname === "/admin" || pathname.startsWith("/admin/"),
        requiresPlatformAdmin: true,
      },
      {
        labelKey: "nav.dashboard",
        to: "/dashboard",
        icon: LayoutDashboard,
        match: (pathname) => pathname === "/dashboard",
      },
      {
        labelKey: "nav.analytics",
        to: "/analytics",
        icon: BarChart3,
        match: (pathname) => pathname === "/analytics",
      },
    ],
  },
  {
    labelKey: "nav.section.commercial",
    items: [
      {
        labelKey: "nav.budgets",
        to: "/budgets",
        icon: ClipboardList,
        match: (pathname) =>
          pathname === "/budgets" ||
          (pathname.startsWith("/budget") &&
            !pathname.includes("/execution") &&
            !pathname.includes("/measurement")),
      },
      {
        labelKey: "nav.templates",
        to: "/templates",
        icon: FolderKanban,
      },
      {
        labelKey: "nav.documents",
        to: "/documents",
        icon: FileText,
      },
    ],
  },
  {
    labelKey: "nav.section.bim",
    items: [
      {
        labelKey: "BIM-IFC",
        to: "/bim",
        icon: Boxes,
        match: (pathname) => pathname === "/bim" || pathname.includes("/mapping/"),
      },
      {
        labelKey: "nav.revitImport",
        to: "/bim/revit-import",
        icon: FileUp,
        match: (pathname) => pathname === "/bim/revit-import",
      },
    ],
  },
  {
    labelKey: "nav.section.execution",
    items: [
      {
        labelKey: "nav.works",
        to: "/works",
        icon: Hammer,
        match: (pathname) => pathname === "/works" || pathname.includes("/execution"),
      },
      {
        labelKey: "nav.measurementAutos",
        to: "/measurement-autos",
        icon: FileCheck2,
        match: (pathname) => pathname === "/measurement-autos" || pathname.includes("/measurement"),
      },
    ],
  },
  {
    labelKey: "nav.section.directory",
    items: [
      {
        labelKey: "nav.costDatabase",
        to: "/cost-database",
        icon: Database,
        match: (pathname) => pathname.startsWith("/cost-database"),
      },
      {
        labelKey: "nav.contacts",
        to: "/contacts",
        icon: Users,
        match: (pathname) => pathname.startsWith("/contacts"),
      },
      {
        labelKey: "nav.settings",
        to: "/settings",
        icon: Settings,
      },
      {
        labelKey: "nav.help",
        to: "/help",
        icon: HelpCircle,
      },
    ],
  },
];

export function AppSidebar({ onNewBudgetClick, className }: AppSidebarProps) {
  const { pathname } = useLocation();
  const { t } = useLanguage();
  const [canViewPlatformAdmin, setCanViewPlatformAdmin] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadPlatformAdminStatus = async () => {
      try {
        const isPlatformAdmin = await getPlatformAdminStatus();

        if (isMounted) {
          setCanViewPlatformAdmin(isPlatformAdmin);
        }
      } catch {
        if (isMounted) {
          setCanViewPlatformAdmin(false);
        }
      }
    };

    void loadPlatformAdminStatus();

    return () => {
      isMounted = false;
    };
  }, []);

  const visibleNavSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.requiresPlatformAdmin || canViewPlatformAdmin),
  }));

  return (
    <Sidebar collapsible="offcanvas" className={cn("border-r border-sidebar-border", className)}>
      <SidebarHeader className="gap-3 px-3 py-4">
        <Link to="/dashboard" className="flex min-h-10 items-center px-1">
          <OrzioLogo size="sm" animated={false} />
        </Link>
        <Button type="button" onClick={onNewBudgetClick} className="h-9 justify-start gap-2">
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("nav.newBudget")}
        </Button>
      </SidebarHeader>

      <SidebarContent className="px-1">
        {visibleNavSections.map((section) => (
          <SidebarGroup key={section.labelKey}>
            <SidebarGroupLabel>{t(section.labelKey)}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => (
                  <SidebarNavMenuItem key={`${section.labelKey}-${item.labelKey}`} item={item} pathname={pathname} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="px-3 py-4">
        <SidebarSeparator className="mx-0" />
        <div className="flex items-center justify-between gap-2 pt-2">
          <span className="truncate text-xs text-sidebar-foreground/65">Workspace Orzio</span>
          <LanguageToggle />
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

interface SidebarNavMenuItemProps {
  item: SidebarNavItem;
  pathname: string;
}

function SidebarNavMenuItem({ item, pathname }: SidebarNavMenuItemProps) {
  const Icon = item.icon;
  const isActive = item.match ? item.match(pathname) : pathname === item.to;
  const { t } = useLanguage();
  const label = item.labelKey.includes(".") ? t(item.labelKey) : item.labelKey;
  const hasSubItems = Boolean(item.subItems?.length);
  const buttonClassName = cn(
    "h-9 border-l-[3px] border-l-transparent",
    isActive && "border-l-primary bg-primary/10 text-primary",
  );

  return (
    <SidebarMenuItem>
      {item.to ? (
        <SidebarMenuButton
          asChild
          isActive={isActive}
          tooltip={label}
          className={buttonClassName}
        >
          <Link to={item.to}>
            <Icon className="h-4 w-4" aria-hidden="true" />
            <span>{label}</span>
          </Link>
        </SidebarMenuButton>
      ) : (
        <SidebarMenuButton
          type="button"
          isActive={isActive}
          tooltip={label}
          className={buttonClassName}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          <span>{label}</span>
        </SidebarMenuButton>
      )}
      {item.badge ? (
        <Badge
          variant="outline"
          className="pointer-events-none absolute right-2 top-1.5 hidden h-6 rounded-md px-1.5 text-[10px] md:inline-flex"
        >
          {item.badge}
        </Badge>
      ) : null}
      {hasSubItems ? (
        <SidebarMenuSub>
          {item.subItems?.map((subItem) => (
            <SidebarNavSubMenuItem
              key={`${item.labelKey}-${subItem.labelKey}`}
              item={subItem}
              pathname={pathname}
            />
          ))}
        </SidebarMenuSub>
      ) : null}
    </SidebarMenuItem>
  );
}

interface SidebarNavSubMenuItemProps {
  item: SidebarNavSubItem;
  pathname: string;
}

function SidebarNavSubMenuItem({ item, pathname }: SidebarNavSubMenuItemProps) {
  const Icon = item.icon;
  const isActive = item.match ? item.match(pathname) : pathname === item.to;
  const { t } = useLanguage();
  const label = item.labelKey.includes(".") ? t(item.labelKey) : item.labelKey;

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild isActive={isActive} size="md">
        <Link to={item.to}>
          {Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
          <span>{label}</span>
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}
